import { useNavigate } from "react-router-dom";
import { FolderOpen, Tag, Settings, X, Compass, BookOpen, Download } from "lucide-react";
import { useAppUpdate } from "@/hooks/useAppUpdate";
import {
  Sheet,
  SheetContent,
  SheetHeader,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import logoWhite from "@/assets/logo-white.png";
import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app"

interface MobileMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscriptionTier?: string;
  onSignOut?: () => void;
}

interface MenuItemProps {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
}

const MenuItem = ({ icon: Icon, label, onClick }: MenuItemProps) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-muted/50 transition-colors rounded-lg"
  >
    <Icon className="h-5 w-5 text-muted-foreground" />
    <span className="font-medium text-foreground">{label}</span>
  </button>
);

export const MobileMenu = ({
  open,
  onOpenChange,
  subscriptionTier = "free",
}: MobileMenuProps) => {
  const navigate = useNavigate();
  const isNativePlatform = Capacitor.isNativePlatform()
  const [versionName, setVersionName] = useState<string>("")
  const { updateAvailable, storeVersion, openStore } = useAppUpdate();

  const handleNavigate = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  useEffect(() => {
    if (isNativePlatform) {
      App.getInfo().then((info) => {
        console.log("App Info:", JSON.stringify(info, null, 2))
        setVersionName(info.version)
      })
    }
  }, [isNativePlatform])

  const tierColors: Record<string, string> = {
    free: "bg-green-800 text-white",
    pro: "bg-blue-500 text-white",
    business: "bg-purple-500 text-white",
    enterprise: "bg-amber-500 text-white",
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className="w-[280px] p-0 rounded-tl-2xl rounded-bl-2xl flex flex-col"
        hideCloseButton
      >
        {/* Header */}
        <SheetHeader className="px-4 pt-6 pb-4 bg-[hsl(15,60%,55%)] text-white rounded-tl-2xl safe-area-inset-top">
          {/* Header with close button */}
          <div className="flex items-start justify-between w-full">
            {/* Logo and App Name */}
            <div className="pt-4 flex items-center gap-3">
              <img
                src={logoWhite}
                alt="Decor Canvas"
                className="h-8 w-auto object-cover"
                style={{ transform: 'scale(1.6)' }}
              />
              <div className="flex flex-col">
                <span className="text-xl font-bold text-white text-left pt-2">Decor Canvas</span>
                {/* <p className="text-white/90 text-sm text-left">Where AI meets aesthetics</p> */}
              </div>
            </div>

            {/* Close Button */}
            {/* <button
              onClick={() => onOpenChange(false)}
              className="pt-3 hover:bg-white/20 rounded-full transition-colors flex-shrink-0"
              aria-label="Close menu"
            >
              <X className="h-5 w-5 text-white" />
            </button> */}
          </div>
        </SheetHeader>

        {/* Menu Items */}
        <div className="p-2 space-y-1 flex-1">
          <MenuItem
            icon={Compass}
            label="Explore"
            onClick={() => handleNavigate("/explore")}
          />
          <MenuItem
            icon={BookOpen}
            label="Blog"
            onClick={() => handleNavigate("/blog")}
          />
          <MenuItem
            icon={FolderOpen}
            label="My Projects"
            onClick={() => handleNavigate("/projects")}
          />
          <MenuItem
            icon={Tag}
            label="Pricing"
            onClick={() => handleNavigate("/pricing")}
          />
          <MenuItem
            icon={Settings}
            label="Settings"
            onClick={() => handleNavigate("/settings")}
          />
          {updateAvailable && (
            <button
              onClick={() => {
                onOpenChange(false);
                openStore();
              }}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-muted/50 transition-colors rounded-lg"
            >
              <div className="relative">
                <Download className="h-5 w-5 text-[hsl(15,60%,55%)]" />
                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-[hsl(15,60%,55%)] ring-2 ring-background" />
              </div>
              <div className="flex flex-col">
                <span className="font-medium text-foreground">App Update</span>
                {storeVersion && (
                  <span className="text-xs text-muted-foreground">v{storeVersion} available</span>
                )}
              </div>
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border safe-area-inset-bottom">
          <div className="flex flex-col items-center">
            <p className="text-xs text-muted-foreground text-center mb-2" style={{ lineHeight: 1 }}>
              Powered by
            </p>
            <span className="text-[1.1rem] font-bold text-[hsl(15,60%,55%)] leading-tight mb-1">Athenia AI Labs</span>
          </div>
          <div className="flex flex-col items-center justify-center gap-1 text-[0.7rem]">
            <div className="flex items-center justify-center gap-1 flex-wrap gap-y-1">
              <button
                onClick={() => handleNavigate("/terms")}
                className="text-muted-foreground hover:text-foreground transition-colors px-1"
                style={{fontSize: '0.7rem'}}>
                Terms & Conditions
              </button>
              <span className="text-muted-foreground">|</span>
              <button
                onClick={() => handleNavigate("/privacy")}
                className="text-muted-foreground hover:text-foreground transition-colors px-1"
                style={{fontSize: '0.7rem'}}>
                Privacy Policy
              </button>
              <span className="text-muted-foreground">|</span>
              <button
                onClick={() => handleNavigate("/support")}
                className="text-muted-foreground hover:text-foreground transition-colors px-1"
                style={{fontSize: '0.7rem'}}>
                Support
              </button>
            </div>
            {versionName && (
              <p className="text-xs text-gray-400 text-center mt-1" translate="no">
                Version {versionName}
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
